import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import { IUpdatePaymentCondition } from "./interfaces/update-payment-condition.interface";
import PaymentConditionAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/masters/payment-condition/payment-condition.entity";
import { ICreatePaymentCondition } from "./interfaces/create-payment-condition.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";
const apiUrl = env.API_UNIT_MEASURE;
const moduleName = "payment_condition";

@Injectable({
  providedIn: "root",
})
export class PaymentConditionService extends AbstractService<
  PaymentConditionAttributes,
  ICreatePaymentCondition,
  IUpdatePaymentCondition
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene condiciones de pago con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Tres objetos (OR lógico) - busca en 'id', 'name', 'type'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: id, name, type (con mapeo de 'contado' → 'CO', 'credito' → 'CR')
   * Array filters: paymentModelId, status
   * Single filters: companyId
   */
  findAllWithCount(
    options: IPagination<PaymentConditionAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<PaymentConditionAttributes>> {

    const hasSearch = !!options.search;

    // Mapeo de búsqueda de tipo: 'contado' → 'CO', 'credito' → 'CR'
    const normalizedSearch = options.search?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const typeSearchValue = normalizedSearch === 'contado' ? 'CO' :
                           normalizedSearch === 'credito' ? 'CR' :
                           options.search ?? "";

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, modelo de pago, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<PaymentConditionAttributes>[] => {
      const deletedFilter: FilterFn<PaymentConditionAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof PaymentConditionAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.paymentModelId && dataToFilter.paymentModelId.length > 0
          ? andWhere('type' as keyof PaymentConditionAttributes, Ops.in(...dataToFilter.paymentModelId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por ID (o solo condiciones si no hay búsqueda)
    const searchByIdQueryObject: FilterFn<PaymentConditionAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<PaymentConditionAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por TYPE (solo si hay búsqueda)
    const searchByTypeQueryObject: FilterFn<PaymentConditionAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("type", Ops.like(typeSearchValue, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<PaymentConditionAttributes>(
      // Expandimos los tres objetos en el buildQuery
      ...searchByIdQueryObject,
      ...searchByNameQueryObject,
      ...searchByTypeQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<PaymentConditionAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<PaymentConditionAttributes[]>> {
    const query = buildQuery<PaymentConditionAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["id", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<PaymentConditionAttributes[]>>(API_URL);
  }
}
